/**
 * Large-sample z-tests for one proportion and for the difference of two.
 *
 * One proportion: z = (p̂ − p₀) / √(p₀(1 − p₀)/n) — the standard error under H₀. Two
 * proportions: z = (p̂₁ − p̂₂) / √(p̄(1 − p̄)(1/n₁ + 1/n₂)) with the pooled p̄, again
 * because H₀ says the two are equal. Neither applies a continuity correction; R's
 * `prop.test` does by default, which is why its χ² (= z² without the correction) and
 * p-value differ slightly from these.
 *
 * Intervals do not assume H₀, so they use unpooled standard errors:
 * - Wald: p̂ ± z·√(p̂(1 − p̂)/n) — the textbook interval, poor near 0 or 1;
 * - Wilson score (Wilson, 1927) for one proportion, which keeps its coverage there;
 * - Newcombe's hybrid score interval (Newcombe, 1998, method 10) for a difference,
 *   built from the two Wilson intervals.
 *
 * Effect size: Cohen's h = 2·asin√p̂₁ − 2·asin√p̂₂ (or against p₀).
 */

import {
  normalCdf,
  normalQuantile,
  normalUpper,
} from "@/lib/stats/distributions";

export interface PValues {
  pTwoSided: number;
  pLess: number;
  pGreater: number;
}

function pValues(z: number): PValues {
  return {
    pTwoSided: Math.min(1, 2 * normalUpper(Math.abs(z))),
    pLess: normalCdf(z),
    pGreater: normalUpper(z),
  };
}

export function cohensH(p1: number, p2: number): number {
  return 2 * Math.asin(Math.sqrt(p1)) - 2 * Math.asin(Math.sqrt(p2));
}

/** Wilson score interval for x successes in n trials. */
export function wilsonInterval(
  x: number,
  n: number,
  level: number
): [number, number] {
  const z = normalQuantile(1 - (1 - level) / 2);
  const p = x / n;
  const z2 = z * z;
  const centre = (p + z2 / (2 * n)) / (1 + z2 / n);
  const half =
    (z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / (1 + z2 / n);
  // At x = 0 and x = n one bound is exactly 0 or 1; computed, it is that ± rounding.
  return [
    x === 0 ? 0 : Math.max(0, centre - half),
    x === n ? 1 : Math.min(1, centre + half),
  ];
}

export interface OneProportionResult extends PValues {
  x: number;
  n: number;
  pHat: number;
  p0: number;
  se0: number;
  z: number;
  zCritical: number;
  wald: [number, number];
  wilson: [number, number];
  level: number;
  h: number;
  /** n·p₀ and n·(1 − p₀): the usual rule wants both ≥ 10. */
  expectedSuccesses: number;
  expectedFailures: number;
}

export function oneProportionZ(
  x: number,
  n: number,
  p0: number,
  level = 0.95
): OneProportionResult | null {
  if (
    !(Number.isInteger(x) && Number.isInteger(n)) ||
    n < 1 ||
    x < 0 ||
    x > n ||
    !(p0 > 0 && p0 < 1)
  ) {
    return null;
  }
  const pHat = x / n;
  const se0 = Math.sqrt((p0 * (1 - p0)) / n);
  const z = (pHat - p0) / se0;
  const zCritical = normalQuantile(1 - (1 - level) / 2);
  const seHat = Math.sqrt((pHat * (1 - pHat)) / n);
  return {
    ...pValues(z),
    x,
    n,
    pHat,
    p0,
    se0,
    z,
    zCritical,
    wald: [pHat - zCritical * seHat, pHat + zCritical * seHat],
    wilson: wilsonInterval(x, n, level),
    level,
    h: cohensH(pHat, p0),
    expectedSuccesses: n * p0,
    expectedFailures: n * (1 - p0),
  };
}

export interface TwoProportionResult extends PValues {
  x1: number;
  n1: number;
  x2: number;
  n2: number;
  p1: number;
  p2: number;
  difference: number;
  pooled: number;
  sePooled: number;
  seUnpooled: number;
  z: number;
  zCritical: number;
  wald: [number, number];
  newcombe: [number, number];
  level: number;
  h: number;
  /** The smallest of n₁p̄, n₁(1 − p̄), n₂p̄, n₂(1 − p̄). */
  minExpected: number;
}

export function twoProportionZ(
  x1: number,
  n1: number,
  x2: number,
  n2: number,
  level = 0.95
): TwoProportionResult | null {
  const valid = [
    [x1, n1],
    [x2, n2],
  ].every(
    ([x, n]) =>
      Number.isInteger(x) && Number.isInteger(n) && n >= 1 && x >= 0 && x <= n
  );
  if (!valid) {
    return null;
  }
  const p1 = x1 / n1;
  const p2 = x2 / n2;
  const pooled = (x1 + x2) / (n1 + n2);
  if (pooled === 0 || pooled === 1) {
    return null;
  }
  const difference = p1 - p2;
  const sePooled = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2));
  const seUnpooled = Math.sqrt((p1 * (1 - p1)) / n1 + (p2 * (1 - p2)) / n2);
  const z = difference / sePooled;
  const zCritical = normalQuantile(1 - (1 - level) / 2);
  const [l1, u1] = wilsonInterval(x1, n1, level);
  const [l2, u2] = wilsonInterval(x2, n2, level);
  return {
    ...pValues(z),
    x1,
    n1,
    x2,
    n2,
    p1,
    p2,
    difference,
    pooled,
    sePooled,
    seUnpooled,
    z,
    zCritical,
    wald: [
      difference - zCritical * seUnpooled,
      difference + zCritical * seUnpooled,
    ],
    newcombe: [
      difference - Math.sqrt((p1 - l1) ** 2 + (u2 - p2) ** 2),
      difference + Math.sqrt((u1 - p1) ** 2 + (p2 - l2) ** 2),
    ],
    level,
    h: cohensH(p1, p2),
    minExpected: Math.min(
      n1 * pooled,
      n1 * (1 - pooled),
      n2 * pooled,
      n2 * (1 - pooled)
    ),
  };
}
